import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useDeviceFingerprint } from "./useDeviceFingerprint";
import { useIPGeolocation } from "./useIPGeolocation";
import { useSecurityLogger } from "./useSecurityLogger";
import { logger } from "@/lib/logger";

/**
 * Hook to detect suspicious activities:
 * - Device fingerprint changes
 * - Unusual geolocation changes
 * - Rapid requests (basic rate limiting)
 */
export function useSuspiciousActivityDetector(user?: any) {
  const deviceFingerprint = useDeviceFingerprint();
  const { geolocation } = useIPGeolocation();
  const { logSecurityEvent } = useSecurityLogger();
  const lastCheckRef = useRef<number>(0);
  const requestCountRef = useRef<number>(0);
  const lastResetRef = useRef<number>(Date.now());

  useEffect(() => {
    const checkForSuspiciousActivity = () => {
      // Defer execution to break out of any auth callbacks
      setTimeout(async () => {
        // Prevent too frequent checks (max once per 5 minutes)
        const now = Date.now();
        if (now - lastCheckRef.current < 300000) return;
        lastCheckRef.current = now;

        if (!user) return;

      try {
        // Check for device fingerprint mismatch
        const { data: lastFingerprint } = await supabase
          .from('device_fingerprints')
          .select('fingerprint_hash, is_trusted, risk_level')
          .eq('user_id', user.id)
          .order('last_seen', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (lastFingerprint && deviceFingerprint) {
          const fingerprintMismatch = lastFingerprint.fingerprint_hash !== deviceFingerprint.fingerprint_hash;
          
          if (fingerprintMismatch && lastFingerprint.is_trusted) {
            await logSecurityEvent({
              event_type: "device_fingerprint_mismatch",
              description: `Device fingerprint changed for user ${user.email}. Possible account compromise or device change.`,
              severity: "medium",
              user_id: user.id,
              was_blocked: false,
              details: {
                previous_fingerprint: lastFingerprint.fingerprint_hash,
                new_fingerprint: deviceFingerprint.fingerprint_hash,
                user_email: user.email,
              },
            });
          }
        }

        // Check for geolocation mismatch (different country)
        const { data: lastLocation } = await supabase
          .from('user_activity')
          .select('geolocation')
          .eq('user_id', user.id)
          .not('geolocation', 'is', null)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (lastLocation?.geolocation && geolocation?.country) {
          const lastGeo = lastLocation.geolocation as any;
          const lastCountry = lastGeo?.country;
          const currentCountry = geolocation.country;
          
          if (lastCountry && currentCountry && lastCountry !== currentCountry) {
            await logSecurityEvent({
              event_type: "geolocation_mismatch",
              description: `Geolocation changed from ${lastCountry} to ${currentCountry} for user ${user.email}`,
              severity: "medium",
              user_id: user.id,
              was_blocked: false,
              details: {
                previous_country: lastCountry,
                new_country: currentCountry,
                previous_location: lastGeo,
                new_location: {
                  country: geolocation.country,
                  city: geolocation.city,
                  region: geolocation.region,
                },
                user_email: user.email,
              },
            });
          }
        }
      } catch (error) {
        logger.error("Suspicious activity check failed", error);
      }
      }, 0);
    };

    checkForSuspiciousActivity();
  }, [deviceFingerprint, geolocation, logSecurityEvent, user]);

  // Basic rate limiting detection
  useEffect(() => {
    const checkRateLimit = () => {
      requestCountRef.current++;
      const now = Date.now();
      const timeSinceReset = now - lastResetRef.current;

      // Reset counter every 60 seconds
      if (timeSinceReset > 60000) {
        requestCountRef.current = 0;
        lastResetRef.current = now;
        return;
      }

      // If more than 100 requests per minute, log suspicious activity
      if (requestCountRef.current > 100 && user) {
        const logRateLimitExceeded = async () => {
          await logSecurityEvent({
            event_type: "rate_limit_exceeded",
            description: `Rate limit exceeded by user ${user.email}: ${requestCountRef.current} requests in last minute`,
            severity: "high",
            user_id: user.id,
            was_blocked: false,
            details: {
              request_count: requestCountRef.current,
              time_window: "60 seconds",
              user_email: user.email,
            },
          });
        };
        logRateLimitExceeded();
      }
    };

    // Track component renders as a proxy for request activity
    checkRateLimit();
  }, [logSecurityEvent]);

  return null;
}
